import { useMemo, useState } from 'react';

import { Bookmark02Icon, Delete02Icon, PencilEdit01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import Alert from 'components/Alert/Alert';
import { Drawer } from 'components/Drawer/Drawer';
import { Button } from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';

import styles from './ManageSavedViewsDrawer.module.scss';
import { SavedViewDefaultToggle } from './SavedViewDefaultToggle';
import { SavedViewDrawerForm } from './SavedViewDrawerForm';
import formStyles from './SavedViewDrawerForm.module.scss';
import { validateSavedViewDrawerForm } from './savedViewDrawerFormValidation';
import { buildReportingFilterPillsFromQueryParams } from './savedViewQueryParams';

const TITLE_KEY = {
  checks: 'reporting.manageChecksViews',
  candidates: 'reporting.manageCandidatesViews',
  referees: 'reporting.manageRefereesViews',
};

export const ManageSavedViewsDrawer = ({
  open,
  onClose,
  savedViews = [],
  activeViewId,
  onLoad,
  onDelete,
  onEditView,
  onSetDefault,
  tab,
}) => {
  const { t } = useTranslation();
  const titleKey = TITLE_KEY[tab] ?? 'reporting.manageSavedViews';

  const [editingView, setEditingView] = useState(null);
  const editFilterPills = useMemo(
    () => buildReportingFilterPillsFromQueryParams(editingView?.savedQueryParams ?? {}),
    [editingView],
  );
  const [editName, setEditName] = useState('');
  const [editErrors, setEditErrors] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);

  const openEdit = view => {
    setEditingView(view);
    setEditName(view.name);
    setEditErrors({});
  };

  const closeEdit = () => {
    setEditingView(null);
    setEditName('');
    setEditErrors({});
  };

  const handleEditSave = async () => {
    const next = validateSavedViewDrawerForm(t, { viewName: editName });
    if (Object.keys(next).length > 0) {
      setEditErrors(next);
      return;
    }

    try {
      await onEditView?.(editingView.id, { name: editName.trim() });
      closeEdit();
    } catch {
      // Request failed; keep edit form open
    }
  };

  const handleLoad = view => {
    onLoad?.(view.id, view.savedQueryParams ?? {});
  };

  const handleClose = () => {
    closeEdit();
    setPendingDelete(null);
    onClose();
  };

  const drawerTitle = editingView ? t('reporting.editViewTitle') : t(titleKey);

  return (
    <>
      <Alert
        show={!!pendingDelete}
        title={t('reporting.deleteViewTitle')}
        message={t('reporting.deleteViewConfirmMessage', { name: pendingDelete?.name ?? '' })}
        ok={t('common.delete')}
        onOk={async () => {
          if (pendingDelete?.id == null) return;
          try {
            await onDelete?.(pendingDelete.id);
            setPendingDelete(null);
          } catch {
            // Keep dialog open for retry
          }
        }}
        type="error"
        cancel={t('common.cancel')}
        onCancel={() => setPendingDelete(null)}
      />

      <Drawer open={open} onClose={handleClose} title={drawerTitle}>
        <Drawer.Body>
          {editingView ? (
            <SavedViewDrawerForm
              filterPills={editFilterPills}
              viewName={editName}
              onViewNameChange={e => {
                setEditName(e.target.value);
                if (editErrors.viewName) setEditErrors(prev => ({ ...prev, viewName: '' }));
              }}
              viewNameError={editErrors.viewName}
            />
          ) : (
            <div className={formStyles.body}>
              {savedViews.length === 0 && (
                <p className={styles.empty}>{t('reporting.noSavedViews')}</p>
              )}
              {savedViews.map(view => (
                <div
                  key={view.id}
                  className={cn(styles.row, {
                    [styles.rowActive]: Number(view.id) === Number(activeViewId),
                  })}
                  onClick={() => handleLoad(view)}>
                  <div className={styles.rowLeft}>
                    <div className={styles.rowMeta}>
                      <div className={styles.rowNameLine}>
                        <HugeiconsIcon
                          icon={Bookmark02Icon}
                          size={15}
                          strokeWidth={1.5}
                          className={styles.bookmarkIcon}
                        />
                        <span className={styles.viewName}>{view.name}</span>
                        {view.isDefault && (
                          <span className={styles.defaultBadge}>{t('reporting.defaultView')}</span>
                        )}
                      </div>
                      <div className={styles.rowSubLine}>
                        <span className={styles.createdAt}>
                          {moment(view.createdAt).format('MMM D, YYYY')}
                        </span>
                        {view.filterPills?.length > 0 && (
                          <>
                            <span className={styles.rowDot}>·</span>
                            <span className={styles.filterCount}>
                              {view.filterPills.length}{' '}
                              {view.filterPills.length === 1 ? 'filter' : 'filters'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.rowActions} onClick={e => e.stopPropagation()}>
                    <SavedViewDefaultToggle
                      viewId={view.id}
                      isDefault={view.isDefault}
                      onSetDefault={onSetDefault}
                    />
                    <IconButton
                      icon={PencilEdit01Icon}
                      prefix="hugeicons"
                      tip={t('reporting.editView')}
                      small
                      onClick={() => openEdit(view)}
                    />
                    <IconButton
                      icon={Delete02Icon}
                      prefix="hugeicons"
                      tip={t('reporting.deleteView')}
                      type="delete"
                      small
                      onClick={() => setPendingDelete({ id: view.id, name: view.name })}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Drawer.Body>

        {editingView && (
          <Drawer.Footer>
            <div className={formStyles.footer}>
              <Button type="secondary" onClick={closeEdit}>
                {t('common.cancel')}
              </Button>
              <Button icon="check" onClick={handleEditSave}>
                {t('common.save')}
              </Button>
            </div>
          </Drawer.Footer>
        )}
      </Drawer>
    </>
  );
};
